import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import { authApi, checkIfTokenExpired } from "@/api"
import { fetchCurrentUser } from "./userSlice"
import { authService } from "@/services/authService"
import type { MenuItem } from "@/types/menu"
import type { MenuPermissionsCookie } from "@/types/menu"

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  hasInitialized: boolean
  sessionId: string | null // Add session ID to force NovuProvider re-mount on login
  menuTree: MenuItem[] // 菜单树
}

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true, // 应用启动时设置为加载状态，避免立即重定向
  error: null,
  hasInitialized: false,
  sessionId: null,
  menuTree: []
}

// 异步检查认证状态
export const checkAuthStatus = createAsyncThunk(
  "auth/checkAuthStatus",
  async (_, { dispatch }) => {
    try {
      // 检查是否有有效的token
      const hasToken = authApi.hasValidToken()

      if (hasToken) {
        // 获取token并检查是否过期
        const token = localStorage.getItem('ACCESS_TOKEN')

        if (token && !checkIfTokenExpired(token)) {
          // Token有效，获取用户数据
          try {
            await dispatch(fetchCurrentUser()).unwrap()
            
            // 集成菜单权限检查
            // 1. 尝试从localStorage读取菜单数据
            const menuData = localStorage.getItem('menu_permissions')
            if (menuData) {
              try {
                const storedData: MenuPermissionsCookie = JSON.parse(menuData)
                const cachedAt = new Date(storedData.cachedAt)
                const now = new Date()
                const cacheAge = now.getTime() - cachedAt.getTime()
                const cacheMaxAge = 3600 * 1000 // 1小时（毫秒）

                // 缓存有效且未过期，直接使用
                if (storedData.menus && storedData.menus.length > 0 && cacheAge < cacheMaxAge) {
                  await dispatch(updateMenuTree(storedData.menus))
                } else {
                  // 缓存过期，重新获取
                  await dispatch(loadUserMenus())
                }
              } catch (parseError) {
                // 数据解析失败，重新获取
                console.warn('Failed to parse menu permissions from localStorage:', parseError)
                await dispatch(loadUserMenus())
              }
            } else {
              // localStorage不存在，调用API获取
              await dispatch(loadUserMenus())
            }
            
            return { isAuthenticated: true }
          } catch {
            // 如果获取用户数据失败，可能是token无效，执行登出
            await authApi.logout()
            return { isAuthenticated: false }
          }
        } else {
          await authApi.logout()
          return { isAuthenticated: false }
        }
      } else {
        return { isAuthenticated: false }
      }
    } catch {
      // 即使发生错误，也返回未认证状态而不是reject
      return { isAuthenticated: false }
    }
  }
)

// 异步登出
export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await authApi.logout()
      // 清除菜单权限数据
      localStorage.removeItem('menu_permissions')
      return {}
    } catch (error) {
      // 即使API调用失败，也清除本地状态
      localStorage.removeItem('menu_permissions')
      return rejectWithValue(error instanceof Error ? error.message : 'Logout failed')
    }
  }
)

// 加载用户菜单权限
export const loadUserMenus = createAsyncThunk(
  "auth/loadUserMenus",
  async (_, { rejectWithValue }) => {
    try {
      const menus = await authService.getUserMenus()

      // 保存菜单数据到localStorage
      const storageData: MenuPermissionsCookie = {
        menus,
        cachedAt: new Date().toISOString()
      }
      localStorage.setItem('menu_permissions', JSON.stringify(storageData))

      return menus
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load menu permissions')
    }
  }
)

// 更新菜单树（从localStorage或其他来源）
export const updateMenuTree = createAsyncThunk(
  "auth/updateMenuTree",
  async (menus: MenuItem[]) => {
    return menus
  }
)

// 清除菜单树
export const clearMenuTree = createAsyncThunk(
  "auth/clearMenuTree",
  async () => {
    return []
  }
)

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload
      state.isLoading = false
      state.error = null
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
      state.isLoading = false
    },
    clearError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) => {
    builder
      // checkAuthStatus
      .addCase(checkAuthStatus.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = action.payload.isAuthenticated
        state.error = null
        state.hasInitialized = true
        // Generate new session ID on successful auth
        if (action.payload.isAuthenticated) {
          state.sessionId = Date.now().toString()
        }
      })
      // logout
      .addCase(logout.pending, (state) => {
        state.isLoading = true
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false
        state.isAuthenticated = false
        state.error = null
        state.sessionId = null // Clear session ID on logout
        state.menuTree = [] // 清除菜单树
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = false // 即使登出失败也重置认证状态
        state.error = action.payload as string
        state.sessionId = null // Clear session ID even if logout fails
        state.menuTree = [] // 清除菜单树
      })
      // loadUserMenus
      .addCase(loadUserMenus.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(loadUserMenus.fulfilled, (state, action) => {
        state.isLoading = false
        state.menuTree = action.payload
        state.error = null
      })
      .addCase(loadUserMenus.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
        // ⚠️ 不清空 menuTree(rbac review FE-M6):硬编码 fallback 已退役,清空 =
        // 后端瞬时失败(重启/网络抖动)时整个侧栏空白,用户只能手动刷新。
        // 保留上一次成功的菜单树(stale 优于 blank;权限收紧由下次成功加载/
        // 重新登录纠正);首次加载就失败时 tree 本来为空,由侧栏空态(含重试
        // 入口)兜底。
      })
      // updateMenuTree
      .addCase(updateMenuTree.fulfilled, (state, action) => {
        state.menuTree = action.payload
      })
      // clearMenuTree
      .addCase(clearMenuTree.fulfilled, (state) => {
        state.menuTree = []
      })
  },
})

export const { setAuthenticated, setLoading, setError, clearError } = authSlice.actions
export default authSlice.reducer